import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../lib/services/AuthService';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const AcceptTerms: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      navigate('/login');
      return;
    }

    // Vérifier si les conditions ont déjà été acceptées
    const checkTermsAcceptance = async () => {
      try {
        const hasAccepted = await AuthService.hasAcceptedTerms(user.id);
        if (hasAccepted) {
          // Si déjà acceptées, rediriger vers le dashboard
          navigate('/');
        } else {
          setLoading(false);
        }
      } catch (err: any) {
        console.error('Erreur lors de la vérification:', err);
        setLoading(false);
      }
    };

    checkTermsAcceptance();
  }, [user, navigate]);

  const handleAccept = async () => {
    if (!acceptedTerms) {
      setError('Vous devez accepter les conditions générales d\'utilisation pour continuer');
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const { error: acceptError } = await AuthService.acceptTerms(user.id);

      if (acceptError) {
        setError(acceptError);
        return;
      }

      // Attendre un peu pour que la base de données se mette à jour
      await new Promise(resolve => setTimeout(resolve, 500));

      // Vérifier que l'acceptation a bien été enregistrée
      const hasAccepted = await AuthService.hasAcceptedTerms(user.id);
      if (!hasAccepted) {
        setError('Erreur : l\'acceptation n\'a pas pu être enregistrée. Veuillez réessayer.');
        return;
      }

      // Rediriger vers le dashboard après acceptation
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-dark-800 rounded-lg p-8 shadow-xl">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">
              Acceptation des conditions générales
            </h1>
            <p className="text-dark-400">
              Pour continuer à utiliser SuiviFinance, vous devez accepter nos conditions générales d'utilisation
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <div className="bg-dark-700 rounded-lg p-6 mb-6 max-h-96 overflow-y-auto">
            <h2 className="text-xl font-semibold text-white mb-4">Conditions d'utilisation</h2>
            <div className="text-dark-300 space-y-4 text-sm">
              <p>
                En utilisant l'application SuiviFinance, vous acceptez d'être lié par les présentes conditions d'utilisation.
                Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'application.
              </p>
              <p>
                SuiviFinance est une application de gestion financière personnelle qui permet aux utilisateurs de suivre
                leurs dépenses, revenus, et de gérer leur budget. L'application est fournie "telle quelle" et nous ne
                garantissons pas qu'elle répondra à vos exigences spécifiques ou qu'elle fonctionnera sans interruption.
              </p>
              <p>
                Nous prenons la confidentialité de vos données très au sérieux. Toutes les données financières que vous
                saisissez dans l'application sont stockées de manière sécurisée et ne sont pas partagées avec des tiers
                sans votre consentement explicite.
              </p>
              <p>
                Pour plus d'informations, veuillez consulter notre{' '}
                <Link to="/terms" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                  page complète des conditions d'utilisation
                </Link>
                {' '}et notre{' '}
                <Link to="/privacy" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                  politique de confidentialité
                </Link>.
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 mb-6">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1 w-5 h-5 text-primary-500 bg-dark-700 border-dark-600 rounded focus:ring-primary-500 focus:ring-2"
              disabled={submitting}
            />
            <label htmlFor="acceptTerms" className="text-sm text-dark-300">
              J'ai lu et j'accepte les{' '}
              <Link to="/terms" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                conditions d'utilisation
              </Link>
              {' '}et la{' '}
              <Link to="/privacy" target="_blank" className="text-primary-400 hover:text-primary-300 underline">
                politique de confidentialité
              </Link>
            </label>
          </div>

          <button
            onClick={handleAccept}
            disabled={submitting || !acceptedTerms}
            className="w-full px-4 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5" />
                <span>Accepter et continuer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptTerms;

